import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PlaybackToggles.module.css';

/** A pyramid metronome with its pendulum. */
function MetronomeIcon() {
  return (
    <svg width={24} height={24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M6.5 21h11L14 3.5h-4z" />
      <path d="M8.6 16h6.8" />
      <path d="M12 16l5.5-10" />
      <circle cx={16} cy={8.7} r={1.4} fill="currentColor" stroke="none" />
    </svg>
  );
}

/** Count-in: a snare drum, two sticks at an angle with their tips near the middle of the head, and the beat's waves rising on the right. */
function CountInIcon() {
  return (
    <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <ellipse cx={12} cy={14.2} rx={9} ry={2.8} />
      <path d="M3 14.2V19.6C3 21.1 7 22.4 12 22.4S21 21.1 21 19.6V14.2" />
      <path d="M7 16.7V20.6M12 17.1V21.6M17 16.7V20.6" />
      <path d="M11 14.2L3.6 4.8M13 14.2L13.2 4.3" />
      <path d="M16.75 7.7A4.5 4.5 0 0 1 18.19 9.02M18 5.54A7 7 0 0 1 20.56 8.1" />
    </svg>
  );
}

function Toggle({ pressed, label, tip, disabled, onClick, children }: { pressed: boolean; label: string; tip: string; disabled?: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      className={`${styles.toggle} ${pressed ? styles.on : ''}`}
      aria-pressed={pressed}
      aria-label={label}
      data-tip={tip}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

interface Props {
  metronome: boolean;
  /** Count in one bar before playing. */
  countIn: boolean;
  disabled?: boolean;
  onMetronome: (on: boolean) => void;
  onCountIn: (on: boolean) => void;
}

/** The metronome and count-in toggles beside the tempo counter, on the practice and rehearsal pages. */
export function PlaybackToggles({ metronome, countIn, disabled, onMetronome, onCountIn }: Props) {
  const { t } = useTranslation();
  return (
    <div className={styles.toggles}>
      <Toggle
        pressed={metronome}
        label={t('practice.metronome')}
        tip={t(metronome ? 'practice.metronomeOff' : 'practice.metronomeOn')}
        disabled={disabled}
        onClick={() => onMetronome(!metronome)}
      >
        <MetronomeIcon />
      </Toggle>
      <Toggle
        pressed={countIn}
        label={t('practice.countIn')}
        tip={t(countIn ? 'practice.countInOff' : 'practice.countInOn')}
        disabled={disabled}
        onClick={() => onCountIn(!countIn)}
      >
        <CountInIcon />
      </Toggle>
    </div>
  );
}
